/* ============================================================
   Room preview - "See it on your wall" (SPIKE)

   <room-preview> (snippets/room-preview.liquid) turns the PDP's selected
   Size + Frame into a 3D canvas at real-world dimensions, textured with the
   design's flat art, exports it on the device and opens the phone's own AR
   viewer anchored to a WALL at true scale. Nothing is uploaded to Shopify.

   The snippet renders the button after the variant picker; placeDock() then moves it
   per arm - B right under the Size options, C right under the main image - with a
   per-arm description and a few tips.

     iPhone / iPad   AR Quick Look  - USDZ built with three.js' USDZExporter,
                     vertical plane anchoring, opened from an <a rel="ar"> that
                     points at a blob: URL (the same mechanism <model-viewer>
                     uses). Quick Look's banner shows title / size / price and
                     a call-to-action that we turn into Add to cart.
     Android         WebXR - GLB built with GLTFExporter, opened through
                     <model-viewer ar-placement="wall">. Scene Viewer would need
                     a hosted file (it can't read blob: URLs), so it isn't used.
                     Two Android rules: the viewer must be loading="eager" (it is
                     kept off-screen, and by default model-viewer only loads when
                     visible), and the WebXR session has to start inside the tap
                     - so the GLB is loaded into the viewer ahead of time and the
                     tap only calls activateAR().
     anything else   button stays hidden (photo-upload fallback is Phase 3).

   Geometry (inches, from the element's data-* attributes):
     stretched canvas   W x H x wrap-depth, art on the front, gallery-wrap
                        sides = the art's edge mirrored around the corner
     floating frame     canvas box flush with the frame's BACK (so recessed at
                        the front by frame-depth - wrap-depth), a gap all round,
                        four frame bars of face width x frame depth in the
                        frame colour, on a thin back plate
     wall               authored upright: back at z = 0, facing +Z, top at +Y.
                        Android/model-viewer places that back against the wall
                        as-is. Quick Look is different: a plane anchor's +Y is
                        the plane's NORMAL (Apple's ARPlaneAnchor), so on a wall
                        +Y points out into the room - an upright model would be
                        pinned to the wall like a shelf. For the USDZ the model
                        is therefore laid on its back (-90 deg about X, the same
                        rotation Apple's plane sample applies): face -> +Y, top
                        -> -Z, which is "up the wall" in ARKit's frame.
                        ?rp_rx=&rp_ry= (degrees) override both angles for
                        on-device checks.
     lighting           Quick Look lights the model itself and looks right.
                        model-viewer over-exposed the art (its own environment
                        plus AR light estimation on a matte material), so on
                        Android the art face is UNLIT (KHR_materials_unlit -
                        the image's exact colours, like a photo of the print)
                        and only the frame bars take scene light; light
                        estimation is off. ?rp_unlit=0 ?rp_exposure=0.7
                        ?rp_xrenv=1 to compare on the device.

   three.js loads on demand from the importmap ("three", "three/addons/"),
   model-viewer only on Android, only when needed. B prepares the model as
   soon as the button is seen or the variant changes, so a tap opens AR right
   away instead of after a spinner. C builds on tap: nothing loads until its
   pop-up opens, and the choices made there cover the wait.

     multi-panel        "72x48 | 36x48 ea." = 3 panels of 36x48 side by side,
                        panel-gap apart, each textured with its slice of the art
                        and wrapped at its own edges. Stretched canvas only.

   A/B CONCLUDED 2026-10-09: B is live for everyone (theme.liquid answers 'B' to every
   visitor; no exposure is pushed any more). As it was tested
   (data-room-preview-ab on <html>, resolved in theme.liquid's head stub):
     control  no button.
     B        the button opens AR straight away; the tips sit under the button.
     C        the button opens a pop-up first (RoomGuide below): size and frame chips
              that drive the PDP picker, the tips, then the launch button. No panel
              choice there (few shoppers want splits); a split picked on the PDP still
              shows in AR and in the pop-up's summary line.
     Both arms use the same AR and count exposure under the same rule, so the
     only difference is direct vs guided.
     ?room_preview_ab=B|C|control forces an arm (30-day cookie); Omniconvert calls
     window.initRoomPreviewAB('B'). Exposure (ab_test: room_preview) is pushed
     from here, for BOTH arms, only when the button could show: AR-capable device
     on a master-product PDP. The Omniconvert goal used-ar-view is sent when AR
     actually launches - arm B's button, arm C's launch button - not when C's pop-up
     opens (that goes to the dataLayer as room_preview_guide_open). Once per visitor
     per 30 days.
   Sets (several designs in one product) are out of scope.
   ?rp_debug=1 prints each step under the button (no DevTools on a phone). */
(() => {
  if (!window.customElements || customElements.get('room-preview')) return;

  const IN = 0.0254; // metres per inch
  const GOAL_COOKIE = 'gooliart_room_preview_used';
  const MODEL_VIEWER_URL = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@4/dist/model-viewer.min.js';

  // The AR model is built at the selected dimensions, so the size line is true.
  const TIPS = [
    'Stand 6\u201310 ft (2\u20133 m) back from the wall',
    'Use good, even lighting',
    'Move your phone slowly across the wall until the artwork appears',
    'Then move around the room to see its actual dimensions from different spots',
  ];
  const COPY = {
    B: { desc: (size) => `Shown at the size you select \u2014 ${size}`, tips: TIPS },
    C: { desc: () => '', tips: [] },   // just "See it on your wall"; choices and tips are in the pop-up
  };

  // Shopper-facing wording avoids "AR": not everyone knows the term.
  const LAUNCH_LABEL = 'View it on my wall';

  const FRAME_COLOURS = {
    black: 0x1c1b1a,
    natural: 0xd9c39d,
    walnut: 0x5b3b2a,
  };

  // --- A/B arm -----------------------------------------------------------
  const arm = () => (window.getRoomPreviewABVariant ? window.getRoomPreviewABVariant() : 'control');

  // Omniconvert goal: first tap per visitor per 30 days (same pattern as used-quick-filter)
  const trackGoal = () => {
    if (document.cookie.indexOf(GOAL_COOKIE + '=1') !== -1) return;
    if (typeof _mktz !== 'object') return;
    _mktz.push(['_Goal', 'used-ar-view', 0]);
    console.log('✅ Omniconvert Goal FIRED: used-ar-view');
    const d = new Date();
    d.setTime(d.getTime() + 30 * 86400000);
    document.cookie = GOAL_COOKIE + '=1;expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  };

  // --- capability ---------------------------------------------------------
  // AR Quick Look: Safari answers relList.supports('ar'). Every other iOS browser
  // is a WKWebView, where that check says false even though Quick Look opens fine -
  // so, like <model-viewer>, the known ones are recognised by user agent: Chrome
  // (CriOS), Edge (EdgiOS), Firefox (FxiOS), the Google app (GSA), DuckDuckGo.
  const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const IS_IOS_THIRDPARTY = IS_IOS && /CriOS\/|EdgiOS\/|FxiOS\/|GSA\/|DuckDuckGo\//.test(navigator.userAgent);
  const quickLookSupported = () => {
    if (!IS_IOS) return false;
    if (IS_IOS_THIRDPARTY) return true;
    const a = document.createElement('a');
    return !!(a.relList && a.relList.supports && a.relList.supports('ar'));
  };
  const webXRSupported = async () => {
    try {
      return !!(navigator.xr && (await navigator.xr.isSessionSupported('immersive-ar')));
    } catch (e) {
      return false;
    }
  };

  // --- selection ----------------------------------------------------------
  // "24x18" -> { w: 24, h: 18, panels: 1 } (inches, width first).
  // "48x36 | 24x36 ea." -> { w: 48, h: 36, panels: 2, panelW: 24, panelH: 36 }:
  // side-by-side slices, panel count from full width / panel width (same rule
  // as the picker's Panels property).
  const dims = (str) => {
    const m = String(str || '').match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  };
  const parseSize = (value) => {
    if (!value) return null;
    const [full, each] = String(value).split('|');
    const f = dims(full);
    if (!f) return null;
    if (!each) return { w: f.w, h: f.h, panels: 1, panelW: f.w, panelH: f.h };
    const e = dims(each);
    if (!e || !e.w) return null;
    const panels = Math.max(1, Math.round(f.w / e.w));
    return { w: f.w, h: f.h, panels, panelW: e.w, panelH: e.h };
  };
  const parseFrame = (value) => {
    const v = String(value || '').toLowerCase();
    if (v.includes('black')) return 'black';
    if (v.includes('natural')) return 'natural';
    if (v.includes('walnut')) return 'walnut';
    if (v.includes('rolled')) return 'rolled';
    return 'stretched';
  };

  const money = (cents) => {
    try {
      const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(cents / 100);
    } catch (e) {
      return '';
    }
  };

  const DEBUG = new URLSearchParams(location.search).get('rp_debug') === '1';
  const withTimeout = (promise, ms, what) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${what} timed out after ${ms / 1000}s`)), ms)),
  ]);

  class RoomPreview extends HTMLElement {
    connectedCallback() {

      this.dock = this.querySelector('[data-room-preview-dock]');
      this.button = this.dock && this.dock.querySelector('[data-room-preview-trigger]');
      if (!this.button) return;
      this.label = this.dock.querySelector('[data-room-preview-label]');
      this.hint = this.dock.querySelector('[data-room-preview-hint]');
      this.note = this.dock.querySelector('[data-room-preview-note]');
      this.tips = this.dock.querySelector('[data-room-preview-tips]');
      this.link = this.querySelector('[data-room-preview-link]');
      this.cfg = {
        art: this.dataset.art,
        artW: +this.dataset.artWidth,
        artH: +this.dataset.artHeight,
        title: this.dataset.title,
        url: this.dataset.url,
        wrapDepth: parseFloat(this.dataset.wrapDepthIn) * IN,
        frameDepth: parseFloat(this.dataset.frameDepthIn) * IN,
        frameFace: parseFloat(this.dataset.frameFaceIn) * IN,
        frameGap: parseFloat(this.dataset.frameGapIn) * IN,
        panelGap: (parseFloat(this.dataset.panelGapIn) || 1.5) * IN,
      };
      this.mode = null;        // 'quick-look' | 'webxr'
      this.prepared = null;    // { key, blobUrl, selection }
      this.preparing = null;   // Promise
      this.texturePromise = null;

      this.onVariantChange = () => this.invalidate();
      this.onClick = (e) => { e.preventDefault(); this.open(); };
      this.onQuickLookMessage = (e) => {
        if (e.data === '_apple_ar_quicklook_button_tapped') this.addToCart();
      };

      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange);
      if (this.onArmChange) document.removeEventListener('room-preview-ab:change', this.onArmChange);
      if (this.button) this.button.removeEventListener('click', this.onClick);
      if (this.dockObserver) { this.dockObserver.disconnect(); this.dockObserver = null; }
      if (this.dock) this.dock.hidden = true;
      if (this.link) this.link.removeEventListener('message', this.onQuickLookMessage);
      this.revoke();
    }

    log(msg) {
      if (!DEBUG) return;
      if (!this.debugEl) {
        this.debugEl = document.createElement('pre');
        this.debugEl.className = 'room-preview__debug';
        (this.dock || this).append(this.debugEl);
      }
      const t = ((performance.now() / 1000) % 1000).toFixed(1);
      this.debugEl.textContent += `${t}s ${msg}\n`;
      console.info('[Room preview]', msg);
    }

    async init() {
      if (quickLookSupported()) this.mode = 'quick-look';
      else if (await webXRSupported()) this.mode = 'webxr';
      this.log(`mode: ${this.mode || 'none'} (ios: ${IS_IOS}, 3rd-party: ${IS_IOS_THIRDPARTY}, xr: ${!!navigator.xr}, ua: ${navigator.userAgent.slice(0, 70)})`);
      if (!this.mode) {
        if (DEBUG) { this.dock.hidden = false; this.button.disabled = true; this.showNote('No AR on this device/browser (needs Safari on iOS, or Chrome with ARCore on Android).'); }
        return;
      }

      // (the A/B is over: no ab_exposure; theme.liquid answers 'B' for everyone)
      this.log(`arm: ${arm()}`);
      this.onArmChange = () => this.applyArm();
      document.addEventListener('room-preview-ab:change', this.onArmChange);
      if (arm() === 'control') return;   // nothing shown (applyArm shows it if the tool assigns later)
      this.show();
    }

    // an experiment tool may assign after load: show or hide live
    applyArm() {
      if (arm() !== 'control' && !this.shown) this.show();
      if (arm() === 'control' && this.shown) { this.dock.hidden = true; this.shown = false; }
      if (this.shown) this.placeDock();
    }

    // --- where the button sits, per arm -------------------------------------------
    // B  right under the Size options, where the shopper decides on the size. Native
    //    products rebuild the whole variant picker on every change, which takes the button
    //    with it, so a MutationObserver puts it back in the same microtask - before the
    //    page repaints, so it never blinks.
    // C  right under the main image: its pop-up has its own size / frame choice, so it
    //    doesn't need to sit by the picker.
    // Anywhere the target isn't found, it stays where the snippet renders it.
    dockTarget() {
      if (arm() === 'B') {
        const picker = this.picker();
        const size = picker && picker.querySelector('input[data-option-name="Size"]');
        return size && size.closest('fieldset, .variant-picker__option');
      }
      if (arm() === 'C') {
        const gallery = document.querySelector(`product-gallery[form="${this.getAttribute('form-id')}"]`);
        return gallery && gallery.querySelector('[data-product-carousel]');
      }
      return null;
    }

    placeDock() {
      const target = this.dockTarget();
      if (target) {
        if (target.nextElementSibling !== this.dock) target.after(this.dock);
      } else if (this.dock.parentElement !== this) {
        this.prepend(this.dock);
      }
      this.dock.dataset.place = target ? (arm() === 'B' ? 'sizes' : 'image') : '';
      if (!this.dockObserver) {
        this.dockObserver = new MutationObserver(() => {
          const t = this.dockTarget();
          if (this.shown && t && t.nextElementSibling !== this.dock) this.placeDock();
        });
        this.dockObserver.observe(this.closest('product-rerender') || this.parentElement, { childList: true, subtree: true });
      }
    }

    show() {
      if (this.shown) return;
      this.shown = true;

      this.hidden = false;
      this.dock.hidden = false;
      this.placeDock();
      this.setAttribute('data-mode', this.mode);
      if (!this.wired) {
        this.wired = true;
        this.button.addEventListener('click', this.onClick);
        this.link.addEventListener('message', this.onQuickLookMessage);
        document.addEventListener('variant:change', this.onVariantChange);
      }
      this.refreshState();

      // B: warm up while the shopper is still choosing, so the tap is instant. C builds on tap
      // instead: its button sits by the main image, in view on load, so warming up would download
      // three.js and the artwork (~0.8 MB) and build the model for every visitor; its pop-up starts
      // the build and the shopper's size / frame choice there covers the wait.
      if (arm() !== 'C' && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((en) => en.isIntersecting)) {
            io.disconnect();
            this.prepare().catch(() => {});
          }
        }, { rootMargin: '200px' });
        io.observe(this.dock);   // not `this`: the host is display:contents, so it has no box and never intersects
      }
    }

    // --- what is selected right now --------------------------------------
    // --- the picker, master or native ---------------------------------------------
    // Masters: <master-variant-picker> keeps every variant in masterVariantsData and the
    // selection in selectedOptions, and never re-renders. Natives: the theme's own
    // <variant-picker> exposes no data and its block is replaced on every change, so the
    // snippet embeds a compact copy of the product's variants and the selection is read
    // off the checked radios. Everything else in this file goes through data() /
    // selectedOptions() / currentVariant() and does not care which one it is.
    picker() {
      const formId = this.getAttribute('form-id');
      return document.querySelector(`master-variant-picker[form-id="${formId}"]`)
        || document.querySelector(`variant-picker[form-id="${formId}"]`)
        || document.querySelector('master-variant-picker, variant-picker');
    }

    // { options: [{ name, values: [string] }], variants: [{ option1, option2, option3, available, price }] }
    data() {
      const picker = this.picker();
      if (picker && picker.masterVariantsData) return picker.masterVariantsData;
      if (this.nativeData === undefined) {
        this.nativeData = null;
        const script = this.querySelector('[data-room-preview-variants]');
        try {
          const raw = JSON.parse(script.textContent);
          this.nativeData = {
            options: raw.options.map((o) => ({ name: o.name, values: (o.values || []).map((v) => (v && v.name) || v) })),
            variants: raw.variants.map((v) => ({ id: v[0], option1: v[1], option2: v[2], option3: v[3], available: !!v[4], price: v[5] })),
          };
        } catch (e) { /* no native data: selection() returns null and the button stays hidden */ }
      }
      return this.nativeData;
    }

    selectedOptions() {
      const picker = this.picker(), data = this.data();
      if (!picker || !data) return null;
      if (picker.masterVariantsData) return picker.selectedOptions;
      const out = {};
      data.options.forEach((opt, i) => {
        const checked = Array.from(picker.querySelectorAll('input[type="radio"]:checked'))
          .find((r) => (r.dataset.optionName || '') === opt.name);
        out[`option${i + 1}`] = checked ? (checked.dataset.optionValue || checked.value) : null;
      });
      return out;
    }

    currentVariant() {
      const picker = this.picker(), data = this.data(), sel = this.selectedOptions();
      if (!picker || !data || !sel) return null;
      if (picker.masterVariantsData) return picker.currentVariant;
      return data.variants.find((v) => v.option1 === sel.option1 && v.option2 === sel.option2 && v.option3 === sel.option3) || null;
    }

    selection() {
      const data = this.data(), selected = this.selectedOptions();
      if (!data || !data.options || !selected) return null;
      const byName = {};
      data.options.forEach((opt, i) => { byName[opt.name] = selected[`option${i + 1}`]; });
      const size = parseSize(byName.Size);
      const frame = parseFrame(byName.Frame);
      const variant = this.currentVariant();
      return {
        sizeLabel: byName.Size || '',
        size,
        frame,
        type: byName.Type || '',
        price: variant && typeof variant.price === 'number' ? variant.price : null,
        key: `${byName.Size}|${frame}|${this.mode}|${JSON.stringify(this.orientation())}|${JSON.stringify(this.renderOptions())}`,
      };
    }


    refreshState() {
      const sel = this.selection();
      if (!sel) return;
      if (!sel.size) {
        this.button.disabled = true;
        this.hint.textContent = 'Not available for this size';
        this.showNote('');
        return;
      }
      this.button.disabled = false;
      this.showNote('');
      const copy = COPY[arm()] || COPY.B;
      this.hint.textContent = copy.desc(this.sizeText(sel));
      this.hint.hidden = !this.hint.textContent;
      if (this.tips && this.tips.dataset.arm !== arm()) {
        this.tips.dataset.arm = arm();
        this.tips.hidden = copy.tips.length === 0;
        this.tips.replaceChildren(...copy.tips.map((t) => { const li = document.createElement('li'); li.textContent = t; return li; }));
      }
      if (this.guide && this.guide.isOpen) this.guide.sync();
    }

    sizeText(sel) {
      const z = sel.size;
      const base = `${z.w}\u2033 \u00d7 ${z.h}\u2033`;
      return z.panels > 1 ? `${base} \u00b7 ${z.panels} panels` : base;
    }

    showNote(text) {
      this.note.textContent = text;
      this.note.hidden = !text;
      if (this.guide && this.guide.isOpen) this.guide.showNote(text);
    }

    invalidate() {
      this.refreshState();
      this.prepared = null;
      this.preparing = null;
      // C builds on tap: nothing until the shopper has opened its pop-up
      if (arm() === 'C' && !this.wanted) return;
      // regenerate quietly for the new selection
      this.prepare().catch(() => {});
    }

    // --- texture -----------------------------------------------------------
    // The flat art, cropped to the selected aspect (centre crop) into a canvas
    // no larger than 2048px: the exporters read pixels, so the image must be
    // CORS-clean (the CDN sends access-control-allow-origin: *).
    loadArt() {
      if (this.texturePromise) return this.texturePromise;
      this.texturePromise = new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('art image failed to load'));
        img.src = this.cfg.art;
      });
      return this.texturePromise;
    }

    async croppedArt(aspect) {
      const img = await this.loadArt();
      const iw = img.naturalWidth, ih = img.naturalHeight;
      let sw = iw, sh = ih;
      if (iw / ih > aspect) sw = Math.round(ih * aspect); else sh = Math.round(iw / aspect);
      const sx = Math.round((iw - sw) / 2), sy = Math.round((ih - sh) / 2);
      const scale = Math.min(1, 2048 / Math.max(sw, sh));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(2, Math.round(sw * scale));
      canvas.height = Math.max(2, Math.round(sh * scale));
      canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      return canvas;
    }

    // --- geometry ------------------------------------------------------------
    // rotation applied to the whole model before export, per AR viewer
    orientation() {
      const q = new URLSearchParams(location.search);
      const def = this.mode === 'quick-look' ? { rx: -90, ry: 0 } : { rx: 0, ry: 0 };
      const rx = q.has('rp_rx') ? parseFloat(q.get('rp_rx')) : def.rx;
      const ry = q.has('rp_ry') ? parseFloat(q.get('rp_ry')) : def.ry;
      return { rx: isFinite(rx) ? rx : def.rx, ry: isFinite(ry) ? ry : def.ry };
    }

    // Android-only rendering choices (Quick Look ignores all of these)
    renderOptions() {
      const q = new URLSearchParams(location.search);
      const num = (k, d) => { const v = parseFloat(q.get(k)); return isFinite(v) ? v : d; };
      return {
        unlit: this.mode === 'webxr' && q.get('rp_unlit') !== '0',
        exposure: num('rp_exposure', 1),
        xrEnvironment: q.get('rp_xrenv') === '1',
      };
    }

    async buildScene(THREE, sel) {
      const S = sel.size;
      const panels = S.panels || 1;
      // splits are stretched canvas only (the picker enforces it); frames apply to singles
      const framed = panels === 1 && sel.frame in FRAME_COLOURS;
      const H = S.h * IN;
      const panelW = S.panelW * IN;
      const gap = panels > 1 ? this.cfg.panelGap : 0;
      const W = panels * panelW + (panels - 1) * gap;   // hung width, gaps included
      const D = this.cfg.wrapDepth;

      // the art is cut across the panels (gaps are not part of the image)
      const canvas = await this.croppedArt((panels * panelW) / H);
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;

      const ro = this.renderOptions();
      // MeshBasicMaterial exports as KHR_materials_unlit in glTF: exact image colours, no lighting
      const art = ro.unlit
        ? new THREE.MeshBasicMaterial({ map: tex })
        : new THREE.MeshStandardMaterial({ map: tex, roughness: 0.85, metalness: 0 });
      const backing = new THREE.MeshStandardMaterial({ color: 0xf1ede6, roughness: 0.9, metalness: 0 });

      const scene = new THREE.Scene();
      const root = new THREE.Group();
      scene.add(root);

      // z of the canvas' back face: flush with the wall, or with the frame's back plate
      const plate = framed ? 0.125 * IN : 0;
      const zBack = plate;

      // PlaneGeometry vertex order: (-x,+y) (+x,+y) (-x,-y) (+x,-y) with uv (0,1) (1,1) (0,0) (1,0)
      const plane = (w, h, mat, pos, rot, uv) => {
        const g = new THREE.PlaneGeometry(w, h);
        if (uv) g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
        const m = new THREE.Mesh(g, mat);
        m.position.set(pos[0], pos[1], pos[2]);
        m.rotation.set(rot[0], rot[1], rot[2]);
        return m;
      };

      // One gallery-wrapped box of width w at centre x, showing the image between u0 and u1.
      // The sides carry the slice's own edge mirrored round the corner.
      const addCanvas = (cx, w, u0, u1) => {
        const du = (u1 - u0) * (D / w), dv = D / H;
        root.add(plane(w, H, art, [cx, 0, zBack + D], [0, 0, 0], [u0, 1, u1, 1, u0, 0, u1, 0]));       // front
        root.add(plane(w, H, backing, [cx, 0, zBack], [0, Math.PI, 0]));                                // back
        root.add(plane(D, H, art, [cx + w / 2, 0, zBack + D / 2], [0, Math.PI / 2, 0],                  // right: front edge -> mirrored inward
          [u1, 1, u1 - du, 1, u1, 0, u1 - du, 0]));
        root.add(plane(D, H, art, [cx - w / 2, 0, zBack + D / 2], [0, -Math.PI / 2, 0],                 // left
          [u0 + du, 1, u0, 1, u0 + du, 0, u0, 0]));
        root.add(plane(w, D, art, [cx, H / 2, zBack + D / 2], [-Math.PI / 2, 0, 0],                     // top
          [u0, 1 - dv, u1, 1 - dv, u0, 1, u1, 1]));
        root.add(plane(w, D, art, [cx, -H / 2, zBack + D / 2], [Math.PI / 2, 0, 0],                     // bottom
          [u0, 0, u1, 0, u0, dv, u1, dv]));
      };

      for (let i = 0; i < panels; i++) {
        const cx = -W / 2 + i * (panelW + gap) + panelW / 2;
        addCanvas(cx, panelW, i / panels, (i + 1) / panels);
      }

      if (framed) {
        const FD = this.cfg.frameDepth, face = this.cfg.frameFace, gap = this.cfg.frameGap;
        const frameMat = new THREE.MeshStandardMaterial({ color: FRAME_COLOURS[sel.frame], roughness: 0.55, metalness: 0 });
        const box = (w, h, d, pos) => {
          const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), frameMat);
          m.position.set(pos[0], pos[1], pos[2]);
          return m;
        };
        const innerW = W + 2 * gap, innerH = H + 2 * gap;
        const outerW = innerW + 2 * face, outerH = innerH + 2 * face;
        root.add(box(innerW, innerH, plate, [0, 0, plate / 2]));                                   // back plate the canvas floats on
        root.add(box(outerW, face, FD, [0, innerH / 2 + face / 2, FD / 2]));                       // top bar
        root.add(box(outerW, face, FD, [0, -(innerH / 2 + face / 2), FD / 2]));                    // bottom bar
        root.add(box(face, innerH, FD, [-(innerW / 2 + face / 2), 0, FD / 2]));                    // left bar
        root.add(box(face, innerH, FD, [innerW / 2 + face / 2, 0, FD / 2]));                       // right bar
      }

      const o = this.orientation();
      root.rotation.set(THREE.MathUtils.degToRad(o.rx), THREE.MathUtils.degToRad(o.ry), 0, 'YXZ');

      // Nothing renders this scene, so nothing computes world matrices for us -
      // without this every face exports at the origin, unrotated.
      scene.updateMatrixWorld(true);
      return scene;
    }

    // --- export ----------------------------------------------------------------
    async prepare() {
      const sel = this.selection();
      if (!sel || !sel.size) return null;
      if (this.prepared && this.prepared.key === sel.key) return this.prepared;
      if (this.preparing && this.preparing.key === sel.key) return this.preparing;

      const job = (async () => {
        const THREE = await import('three');
        const scene = await this.buildScene(THREE, sel);
        let blob;
        if (this.mode === 'quick-look') {
          const { USDZExporter } = await import('three/addons/exporters/USDZExporter.js');
          const bytes = await new USDZExporter().parseAsync(scene, {
            ar: { anchoring: { type: 'plane' }, planeAnchoring: { alignment: 'vertical' } },
            quickLookCompatible: true,
            maxTextureSize: 2048,
          });
          blob = new Blob([bytes], { type: 'model/vnd.usdz+zip' });
        } else {
          const { GLTFExporter } = await import('three/addons/exporters/GLTFExporter.js');
          const buffer = await new GLTFExporter().parseAsync(scene, { binary: true });
          blob = new Blob([buffer], { type: 'model/gltf-binary' });
        }
        this.revoke();
        const result = { key: sel.key, blobUrl: URL.createObjectURL(blob), selection: sel, bytes: blob.size };
        this.log(`${this.mode === 'quick-look' ? 'USDZ' : 'GLB'} built: ${(blob.size / 1e6).toFixed(1)} MB for ${sel.key.split('|').slice(0, 2).join(' / ')}`);
        if (this.mode === 'webxr') {
          await this.loadIntoViewer(result.blobUrl);
          result.loaded = true;
        }
        this.prepared = result;
        this.preparing = null;
        return result;
      })();
      job.key = sel.key;
      this.preparing = job;
      return job;
    }

    revoke() {
      if (this.prepared && this.prepared.blobUrl) {
        try { URL.revokeObjectURL(this.prepared.blobUrl); } catch (e) {}
      }
    }

    // --- open -------------------------------------------------------------------
    // the PDP button
    async open() {
      if (arm() === 'C') {
        this.guide = this.guide || new RoomGuide(this);
        this.guide.open();
        try { (window.dataLayer = window.dataLayer || []).push({ event: 'room_preview_guide_open' }); } catch (e) {}
        return;
      }
      return this.launch();
    }

    // open AR for the current selection (arm B's button, arm C's pop-up button)
    async launch() {
      const sel = this.selection();
      if (!sel || !sel.size) return;
      this.log(`launch (${this.mode}); ready: ${!!(this.prepared && this.prepared.key === sel.key)}`);
      trackGoal();
      this.busy(true);
      try {
        // WebXR must start inside the tap; if the model isn't ready yet, build it
        // now and ask for one more tap rather than start a session too late.
        if (this.mode === 'webxr' && !(this.prepared && this.prepared.key === sel.key)) {
          await withTimeout(this.prepare(), 60000, 'preparing the model');
          this.showNote('Ready \u2014 tap again to open it on your wall.');
          return;
        }
        const model = await withTimeout(this.prepare(), 60000, 'preparing the model');
        if (!model) return;
        this.track(model);
        if (this.mode === 'quick-look') this.openQuickLook(model);
        else await this.openWebXR(model);
      } catch (err) {
        console.error('[Room preview]', err);
        this.log(`error: ${err && err.message}`);
        this.showNote(`Couldn\u2019t open the preview: ${(err && err.message) || 'unknown error'}`);
      } finally {
        this.busy(false);
      }
    }

    openQuickLook(model) {
      const sel = model.selection;
      const frameName = sel.frame in FRAME_COLOURS
        ? `${sel.frame[0].toUpperCase()}${sel.frame.slice(1)} floating frame`
        : 'Stretched canvas';
      const params = new URLSearchParams();
      params.set('allowsContentScaling', '0');            // the whole point: true size, not resizable
      params.set('checkoutTitle', this.cfg.title);
      params.set('checkoutSubtitle', `${this.sizeText(sel)} \u00b7 ${sel.size.panels > 1 ? 'Stretched canvas' : frameName}`);
      if (sel.price != null) params.set('price', money(sel.price));
      params.set('callToAction', 'Add to cart');
      params.set('canonicalWebPageURL', this.cfg.url);
      this.link.setAttribute('href', `${model.blobUrl}#${params.toString()}`);
      this.link.hidden = false;
      this.link.click();
      this.link.hidden = true;
    }

    async ensureViewer() {
      if (!customElements.get('model-viewer')) {
        this.log('loading model-viewer…');
        await withTimeout(import(MODEL_VIEWER_URL), 30000, 'model-viewer download');
        this.log('model-viewer ready');
      }
      if (this.viewer) return this.viewer;
      const v = document.createElement('model-viewer');
      v.setAttribute('ar', '');
      v.setAttribute('ar-modes', 'webxr');
      v.setAttribute('ar-placement', 'wall');
      v.setAttribute('ar-scale', 'fixed');
      v.setAttribute('loading', 'eager');      // off-screen: the default only loads when visible
      v.setAttribute('reveal', 'auto');
      v.setAttribute('shadow-intensity', '0');
      v.setAttribute('tone-mapping', 'neutral');
      const ro = this.renderOptions();
      v.setAttribute('exposure', String(ro.exposure));
      if (ro.xrEnvironment) v.setAttribute('xr-environment', '');
      this.log(`viewer: unlit=${ro.unlit} exposure=${ro.exposure} xr-environment=${ro.xrEnvironment}`);
      v.style.cssText = 'position:fixed;left:0;top:0;width:320px;height:320px;opacity:0;pointer-events:none;z-index:-1;';
      v.addEventListener('ar-status', (e) => {
        const st = e.detail && e.detail.status;
        this.log(`ar-status: ${st}`);
        if (st === 'failed') this.showNote('The wall view couldn\u2019t start on this phone. Installing \u201cGoogle Play Services for AR\u201d from the Play Store usually fixes it \u2014 then tap again.');
        if (st === 'session-started' || st === 'object-placed') this.showNote('');
      });
      v.addEventListener('error', (e) => this.log(`viewer error: ${(e.detail && e.detail.type) || 'unknown'}`));
      document.body.appendChild(v);
      this.viewer = v;
      return v;
    }

    async loadIntoViewer(blobUrl) {
      const v = await this.ensureViewer();
      if (v.getAttribute('src') === blobUrl && v.loaded) return;
      this.log('loading GLB into viewer…');
      await withTimeout(new Promise((resolve, reject) => {
        const done = () => { cleanup(); resolve(); };
        const fail = (e) => { cleanup(); reject(new Error('model failed to load: ' + ((e.detail && e.detail.type) || ''))); };
        const cleanup = () => { v.removeEventListener('load', done); v.removeEventListener('error', fail); };
        v.addEventListener('load', done);
        v.addEventListener('error', fail);
        v.setAttribute('src', blobUrl);
      }), 30000, 'GLB load');
      this.log(`viewer loaded (canActivateAR: ${v.canActivateAR})`);
    }

    async openWebXR(model) {
      if (!model.loaded) await this.loadIntoViewer(model.blobUrl);
      this.log('activateAR()');
      await this.viewer.activateAR();
      this.log('activateAR returned');
    }

    addToCart() {
      if (this.guide && this.guide.isOpen) this.guide.close();
      const formId = this.getAttribute('form-id');
      const form = document.getElementById(formId);
      const submit = form && (form.querySelector('buy-buttons button[type="submit"]') || document.querySelector(`button[type="submit"][form="${formId}"]`));
      if (submit) submit.click();
    }

    busy(on) {
      if (this.guide && this.guide.isOpen) this.guide.setBusy(on);
      this.button.disabled = on;
      this.button.classList.toggle('is-busy', on);
      this.label.textContent = on ? 'Preparing…' : 'See it on your wall';
    }

    track(model, modeOverride) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'room_preview_open',
          room_preview_mode: modeOverride || this.mode,
          room_preview_size: model.selection.sizeLabel,
          room_preview_frame: model.selection.frame,
          room_preview_bytes: model.bytes,
        });
      } catch (e) {}
    }
  }

  /* ---------------------------------------------------------------------------
     RoomGuide - arm C's pop-up, shown before AR opens: what's selected, size /
     frame chips, the tips, and the button that launches AR. Size chips are single
     canvases only, so a tap on one also leaves a split picked on the PDP.
     The chips click the PDP picker's own inputs, so the page, the price and the
     cart follow; the picker's variant:change then rebuilds the AR model, and the
     launch button is ready again a moment later.
     --------------------------------------------------------------------------- */
  class RoomGuide {
    constructor(host) {
      this.host = host;
      this.isOpen = false;
    }

    build() {
      if (this.root) return;
      const root = document.createElement('div');
      root.className = 'room-guide';
      root.hidden = true;
      root.innerHTML = `
        <div class="room-guide__backdrop" data-close></div>
        <div class="room-guide__sheet" role="dialog" aria-modal="true" aria-labelledby="room-guide-title">
          <button type="button" class="room-guide__close" data-close aria-label="Close">×</button>
          <div class="room-guide__head">
            <img class="room-guide__thumb" alt="" data-thumb>
            <div>
              <h2 class="room-guide__title h4" id="room-guide-title">See it on your wall</h2>
              <p class="room-guide__summary" data-summary></p>
            </div>
          </div>
          <div class="room-guide__body">
          <div class="room-guide__group" data-group="Size"><p class="room-guide__label">Size</p><div class="room-guide__scroller"><div class="room-guide__chips" data-sizes></div><button type="button" class="room-guide__more" data-more aria-label="More sizes" hidden><svg viewBox="0 0 12 12" aria-hidden="true"><path d="M4.5 2.5 8 6l-3.5 3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button></div></div>
          <div class="room-guide__group" data-group="Frame"><p class="room-guide__label">Frame</p><div class="room-guide__chips" data-frames></div></div>
          <details class="room-guide__tipsbox">
            <summary>Tips for the best result</summary>
            <ul class="room-preview__tips" data-tips></ul>
          </details>
          </div>
          <div class="room-guide__footer">
            <p class="room-preview__note" data-note aria-live="polite"></p>
            <button type="button" class="button w-full room-guide__launch" data-launch><span data-launch-label>${LAUNCH_LABEL}</span></button>
          </div>
        </div>`;
      document.body.appendChild(root);
      this.root = root;
      this.sizesEl = root.querySelector('[data-sizes]');
      this.framesEl = root.querySelector('[data-frames]');
      this.summary = root.querySelector('[data-summary]');
      this.note = root.querySelector('[data-note]');
      this.launchBtn = root.querySelector('[data-launch]');
      this.launchLabel = root.querySelector('[data-launch-label]');
      root.querySelector('[data-thumb]').src = this.host.cfg.art.replace(/width=\d+/, 'width=240');
      root.querySelector('[data-tips]').replaceChildren(...TIPS.map((t) => { const li = document.createElement('li'); li.textContent = t; return li; }));
      root.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.launchBtn.addEventListener('click', () => this.host.launch());
      this.sizesEl.addEventListener('scroll', () => this.updateMore(), { passive: true });
      root.querySelector('[data-more]').addEventListener('click', () => this.sizesEl.scrollBy({ left: this.sizesEl.clientWidth * 0.6, behavior: 'smooth' }));
      this.onKey = (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); };
    }

    open() {
      this.build();
      this.isOpen = true;
      this.root.hidden = false;
      document.documentElement.classList.add('room-guide-open');
      document.addEventListener('keydown', this.onKey);
      this.showNote('');
      this.renderChips();
      this.sync();
      this.host.wanted = true;   // from here on, choices in the pop-up rebuild the model (host.invalidate)
      this.host.prepare().catch(() => {});
      this.launchBtn.focus({ preventScroll: true });
    }

    close() {
      this.isOpen = false;
      this.root.hidden = true;
      document.documentElement.classList.remove('room-guide-open');
      document.removeEventListener('keydown', this.onKey);
      this.host.button.focus({ preventScroll: true });
    }

    // The slot is always in the layout (fixed height in CSS) and only its text changes:
    // the theme forces [hidden] to display:none !important, so toggling `hidden` made the
    // launch button jump every time a message came or went.
    showNote(text) {
      if (!this.note) return;
      this.note.textContent = text || '';
    }

    setBusy(on) {
      if (!this.launchBtn) return;
      this.launchBtn.disabled = on;
      this.launchLabel.textContent = on ? 'Preparing…' : LAUNCH_LABEL;
    }

    // --- chips drive the PDP picker ---------------------------------------------
    sizeValues() {
      const data = this.host.data();
      const opt = data && data.options.find((o) => o.name === 'Size');
      return opt ? opt.values.map((v) => (v && v.name) || v).map((v) => ({ value: v, size: parseSize(v) })).filter((x) => x.size) : [];
    }

    // Frame tiles reuse the PDP picker's own swatch photos (CSS backgrounds on
    // label.color-swatch, store-wide files) so the pop-up matches what the shopper just
    // saw and follows any change to them.
    swatchUrl(value) {
      const picker = this.host.picker();
      const input = Array.from(picker.querySelectorAll('input[type="radio"]')).find((r) => (r.dataset.optionName || '') === 'Frame' && (r.dataset.optionValue || r.value) === value);
      const label = input && (input.nextElementSibling || picker.querySelector(`label[for="${CSS.escape(input.id)}"]`));
      const m = label && getComputedStyle(label).backgroundImage.match(/url\(["']?([^"')]+)["']?\)/);
      if (!m) return null;
      try { const u = new URL(m[1], location.href); u.searchParams.set('width', '160'); return u.toString(); } catch (e) { return m[1]; }
    }

    renderChips() {
      const data = this.host.data();
      if (!data) return;
      const make = (kind, value, inner, extraClass) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'room-guide__chip' + (extraClass ? ' ' + extraClass : '');
        b.dataset.kind = kind; b.dataset.value = value;
        b.innerHTML = inner;
        b.addEventListener('click', () => {
          if (kind === 'Size') this.pickSize(value);
          else this.pickFrame(value);
        });
        return b;
      };
      const esc = (t) => String(t).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
      const all = this.sizeValues();
      this.sizesEl.replaceChildren(...all.filter((x) => x.size.panels === 1)
        .map((x) => make('Size', x.value, `<span class="room-guide__chiptext">${x.size.w}″×${x.size.h}″</span>`)));
      const frame = data.options.find((o) => o.name === 'Frame');
      this.framesEl.replaceChildren(...(frame ? frame.values.map((v) => v.name || v) : []).filter((v) => parseFrame(v) !== 'rolled').map((v) => {
        const f = parseFrame(v), name = f === 'stretched' ? 'Stretched' : f[0].toUpperCase() + f.slice(1);
        const url = this.swatchUrl(v);
        const pic = url ? `<span class="room-guide__swatch" style="background-image:url('${esc(url)}')"></span>`
          : `<span class="room-guide__swatch" style="background:#${(FRAME_COLOURS[f] || 0xe9e6df).toString(16).padStart(6, '0')}"></span>`;
        return make('Frame', v, `${pic}<span class="room-guide__chiptext">${esc(name)}</span>`, 'room-guide__chip--tile');
      }));
    }

    // Mark combinations the PDP picker marks: dimmed with a diagonal line (its .is-disabled
    // look), for the same reason - not sold with what is currently selected. Unlike the PDP,
    // where tapping one silently reverts, these stay tappable and switch to a real variant
    // (pickSize / pickFrame), so nothing is ever a dead end.
    updateAvailability() {
      const sel = this.host.selection(); if (!sel || !sel.size) return;
      const cur = this.current();
      this.root.querySelectorAll('.room-guide__chip').forEach((b) => {
        let ok = true;
        if (b.dataset.kind === 'Size') ok = this.exists(b.dataset.value, cur.Frame);
        if (b.dataset.kind === 'Frame') ok = this.exists(cur.Size, b.dataset.value);
        b.classList.toggle('is-unavailable', !ok && !b.classList.contains('is-active'));
      });
    }

    // the size row scrolls sideways: show the PDP's ">" on its right edge until the end is reached
    updateMore() {
      const row = this.sizesEl, more = this.root.querySelector('[data-more]'); if (!row || !more) return;
      more.hidden = row.scrollWidth - row.clientWidth - row.scrollLeft < 8;
    }

    // --- what the master actually sells ---------------------------------------------
    // Checked against all six ratios: the largest size has no framed variants, splits
    // are Stretched only and exist for the top two sizes of 3:2, 4:3 and 2:1 (2:3, 3:4
    // and square have none). Read from the variants
    // rather than hard-coded, so a change in the masters is picked up.
    exists(size, frame) {
      const d = this.host.data(), selected = this.host.selectedOptions();
      if (!d || !selected) return false;
      const names = d.options.map((o) => o.name);
      const key = (n) => `option${names.indexOf(n) + 1}`;
      const type = selected[key('Type')];
      return d.variants.some((v) => v[key('Size')] === size && v[key('Frame')] === frame && (!type || v[key('Type')] === type) && v.available);
    }

    frameValues() {
      const d = this.host.data();
      const opt = d && d.options.find((o) => o.name === 'Frame');
      return opt ? opt.values.map((v) => (v && v.name) || v) : [];
    }

    stretchedValue() { return this.frameValues().find((f) => parseFrame(f) === 'stretched'); }

    current() {
      const d = this.host.data(), selected = this.host.selectedOptions(), out = {};
      if (d && selected) d.options.forEach((o, i) => { out[o.name] = selected[`option${i + 1}`]; });
      return out;
    }

    // reflect the picker: active chips and the summary line. Nothing is disabled -
    // a tap always lands on something real (see pickSize / pickFrame).
    sync() {
      const sel = this.host.selection(); if (!sel || !sel.size) return;
      const cur = this.current(), z = sel.size;
      this.root.querySelectorAll('.room-guide__chip').forEach((b) => {
        let on = false;
        if (b.dataset.kind === 'Size') { const c = parseSize(b.dataset.value); on = c.w === z.w && c.h === z.h; }
        if (b.dataset.kind === 'Frame') on = cur.Frame === b.dataset.value;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.disabled = false;
      });
      this.updateAvailability();
      this.updateMore();
      const frameName = sel.frame in FRAME_COLOURS ? `${sel.frame[0].toUpperCase()}${sel.frame.slice(1)} frame`
        : sel.frame === 'rolled' ? 'Rolled (shown stretched)' : 'Stretched canvas';
      this.summary.textContent = `${this.host.sizeText(sel)} · ${z.panels > 1 ? 'Stretched canvas' : frameName}`;
    }

    // A size the current frame doesn't come in: switch to Stretched first (the picker
    // reverts a click on a combination that doesn't exist, so the order matters).
    pickSize(value) {
      const cur = this.current();
      let note = '';
      if (!this.exists(value, cur.Frame)) {
        const wrap = this.stretchedValue();
        if (wrap && this.exists(value, wrap)) {
          const z = parseSize(value);
          note = `${z.w}″×${z.h}″ comes as a stretched canvas, so the frame was switched.`;
          this.pick('Frame', wrap);
        }
      }
      this.pick('Size', value, note);
    }

    // A frame the current size doesn't come in (the largest size, or a split): step down
    // to the biggest single size that is sold with that frame.
    pickFrame(value) {
      const cur = this.current();
      let note = '';
      if (!this.exists(cur.Size, value)) {
        const singles = this.sizeValues().filter((x) => x.size.panels === 1 && this.exists(x.value, value))
          .sort((a, b) => b.size.w * b.size.h - a.size.w * a.size.h);
        if (!singles.length) return this.host.showNote('That frame isn’t available for this artwork right now.');
        const t = singles[0];
        note = `Frames go up to ${t.size.w}″×${t.size.h}″, so the size was switched.`;
        // leave a split first: its frame is locked to Stretched
        this.pick('Size', t.value);
      }
      this.pick('Frame', value, note);
    }

    // Click the PDP picker's own input, then check it took: the picker silently reverts
    // a click on a combination that doesn't exist, and a dead tap must never be silent.
    pick(optionName, value, note) {
      const picker = this.host.picker(); if (!picker) return false;
      const input = Array.from(picker.querySelectorAll('input[type="radio"]'))
        .find((i) => (i.dataset.optionName || '') === optionName && (i.dataset.optionValue || i.value) === value);
      if (!input) { this.host.showNote('That option isn’t available for this artwork.'); return false; }
      if (!input.checked) input.click();   // variant:change -> host.invalidate() -> refreshState() -> sync()
      const took = this.current()[optionName] === value;
      this.host.showNote(took ? (note || '') : 'That combination isn’t available — try another size or frame.');
      // Reflect the tap now: the radio is checked the moment it's clicked, but a native product's
      // variant:change only fires after the section round trip (~0.4 s+ on mobile), and the chips
      // waited for it. variant:change still re-syncs when it lands, so nothing can drift.
      this.sync();
      return took;
    }
  }

  customElements.define('room-preview', RoomPreview);
})();
